import { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';
import { canonicalOrgsApi } from '../lib/api';
import { Loader2 } from 'lucide-react';

/**
 * OrgAutocomplete
 * ---------------
 * Drop-in replacement for the freeform organisation-name input. Searches the
 * canonical organisations DB after 3 chars (contains-match), filtered by the
 * org type the user has already chosen.
 *
 * Props:
 *  - value: current text in the field
 *  - onChange(text): called as the user types
 *  - onPick(org): called when the user selects a canonical record. Parent
 *      should pre-fill email/address/website/canonical_org_id from it. If
 *      the typed text only matched one of the record's `aliases` — an old/
 *      defunct name, OR a still-live organisation that has since delegated
 *      this function elsewhere (see backend/app/models/uk_councils_
 *      seed_data.py) — `org.matched_alias` carries that alias text so the
 *      parent can tell the user and pass it through to the SAR letter.
 *  - type: optional org type filter
 *  - placeholder, className, testId, autoFocus
 *
 * The component does not manage `onPick` side-effects beyond calling back.
 */
const contains = (haystack, needle) =>
  (haystack || '').toLowerCase().includes((needle || '').toLowerCase());

// The typed text matched this result only through an old/defunct name, not
// its current one — mirrors the backend search's contains-match on name OR
// aliases (GET /canonical-orgs/search), so this only fires when the live
// name itself wouldn't have surfaced the result.
const matchedAliasFor = (query, org) => {
  const q = (query || '').trim();
  if (!q || contains(org.name, q)) return null;
  return (org.aliases || []).find((a) => contains(a, q)) || null;
};
const OrgAutocomplete = ({
  value,
  onChange,
  onPick,
  onBlur,
  type,
  placeholder = 'Start typing to see matches',
  className = '',
  testId = 'org-autocomplete',
  autoFocus,
}) => {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const text = (value || '').trim();
    if (text.length < 3) {
      setResults([]);
      setOpen(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const res = await canonicalOrgsApi.search(text, type);
        if (cancelled) return;
        setResults(res.data?.results || []);
        setOpen(true);
        setHighlighted(0);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [value, type]);

  // Click-outside dismissal
  useEffect(() => {
    const handle = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const pick = (org) => {
    const matchedAlias = matchedAliasFor(value, org);
    onChange(org.name);
    onPick?.(matchedAlias ? { ...org, matched_alias: matchedAlias } : org);
    setOpen(false);
  };

  const handleKey = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter' && results[highlighted]) {
      e.preventDefault();
      pick(results[highlighted]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <Input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        onKeyDown={handleKey}
        onFocus={() => results.length > 0 && setOpen(true)}
        placeholder={placeholder}
        className={className}
        autoFocus={autoFocus}
        data-testid={testId}
      />
      {loading && (
        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-55 animate-spin" aria-hidden="true" />
      )}
      {open && results.length > 0 && (
        <div
          className="absolute z-50 mt-1 w-full bg-surface rounded-lg shadow-overlay overflow-hidden max-h-72 overflow-y-auto"
          data-testid={`${testId}-dropdown`}
        >
          {results.map((org, idx) => (
            <button
              key={org.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(org)}
              onMouseEnter={() => setHighlighted(idx)}
              className={`w-full text-left px-3 py-2.5 flex items-start gap-2.5 transition-colors duration-state ${
                idx === highlighted ? 'bg-ground' : 'hover:bg-ground'
              }`}
              data-testid={`${testId}-result-${idx}`}
            >
              <div className="flex-1 min-w-0">
                <p className="text-[15px] font-medium text-ink truncate">{org.name}</p>
                <p className="text-sm text-ink-55 truncate">
                  {org.privacy_email || 'No privacy email on file'}
                  {org.admin_verified ? ' · verified' : ' · added by another user, not yet checked'}
                </p>
                {matchedAliasFor(value, org) && (
                  <p className="text-sm text-ink-55 truncate">
                    Handles records for &ldquo;{matchedAliasFor(value, org)}&rdquo;
                  </p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrgAutocomplete;
